import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { Button, FormGroup, Modal } from 'react-bootstrap';
import { MOBILE_VIEWPORT } from 'utils/constants';
import closeIcon from 'v2/icons/close-icon.svg';
import { ProposalEntity, SearchEntity } from 'v2/models';
import styles from './proposalRespondModal.module.scss';
import SearchProviderFilterView from './SearchProviderFilterView';
import MarkdownRender from '../../../../../components/MarkdownRender/MarkdownRender';
import NomadGuidance from '../../NomadGuidance/NomadGuidance';
import { useFormProposalResponse } from 'v2/queries/useFormProposalResponse';
import ProposalRespondContent from './ProposalRespondContent';
import { FormProvider } from 'react-hook-form';
import FooterButtons from './FooterButtons';

interface ProposalRespondModalOwnProps {
  proposal: ProposalEntity;
  show: boolean;
  onHide: () => void;
}

const ProposalRespondModal = ({
  show,
  onHide,
  proposal,
}: ProposalRespondModalOwnProps) => {
  const [scrollableValue, setScrollableValue] = useState(
    window.innerWidth > MOBILE_VIEWPORT ? true : false
  );
  const [showParameters, setShowParameters] = useState(false);
  const search = proposal.search as SearchEntity;

  const { formContext, onSubmit, isBusy, isCheckSellerResponseEnabled } =
    useFormProposalResponse({ proposal, onSettled: onHide });

  const { handleSubmit, register, watch } = formContext;

  useEffect(() => {
    register('data_files');
  }, [register]);

  const checkResponse = watch('check_seller_response_status');

  const showTooltip = isCheckSellerResponseEnabled && !checkResponse && !isBusy;

  window.addEventListener('resize', () => {
    setScrollableValue(window.innerWidth > MOBILE_VIEWPORT ? true : false);
  });

  return (
    <Modal
      className={styles.root}
      size="xl"
      centered
      show={show}
      onHide={onHide}
      scrollable={scrollableValue}
    >
      <form
        className={classnames('modal-content', styles.modalContent)}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Modal.Body className={classnames('py-0', styles.modalBody)}>
          <Button className={styles.closeBtn} variant="link" onClick={onHide}>
            <span className="d-none d-lg-block">Close</span>
            <img
              className="d-block d-lg-none"
              src={closeIcon}
              alt="closeProposalResponse"
            ></img>
          </Button>
          <div className="d-flex flex-column flex-lg-row">
            <div
              className={classnames(
                'px-0 px-lg-3 py-4 w-100 w-lg-50',
                styles.leftSide
              )}
            >
              <h1>Data Request</h1>
              <p
                className={classnames('text-light8', styles.sectionDescription)}
              >
                Please review the details of the data request below.
              </p>

              <div className="pt-0 pt-lg-5">
                <FormGroup className="mb-0">
                  <label>Name</label>
                </FormGroup>
                <p className="m-0">{search.name}</p>
              </div>

              <div className="pt-4 pt-lg-5">
                <FormGroup className="mb-0">
                  <label>Description</label>
                </FormGroup>
                <MarkdownRender
                  className="markdown-text-blue-light pr-4"
                  source={search.description}
                  format={true}
                />
              </div>

              <div className="pt-4 pt-lg-5">
                <FormGroup className="mb-0">
                  <label>Client Description</label>
                </FormGroup>
                <p className="m-0">
                  {proposal.data_buyer.short_description ||
                    proposal.data_buyer.vague_description}
                </p>
              </div>

              {search.filters.length ? (
                <div className="pt-3">
                  <FormGroup className="mb-3 d-flex align-items-baseline">
                    <label>Requirements ({search.filters.length})</label>
                    <Button
                      variant="link"
                      className="p-0 ml-2"
                      onClick={() => setShowParameters(!showParameters)}
                    >
                      {showParameters ? 'Hide' : 'Show'}
                    </Button>
                  </FormGroup>
                  {showParameters ? (
                    <SearchProviderFilterView filters={search.filters} />
                  ) : null}
                </div>
              ) : null}
              <NomadGuidance proposal={proposal} />
            </div>
            <FormProvider {...formContext}>
              <ProposalRespondContent proposal={proposal} />
            </FormProvider>
          </div>
        </Modal.Body>
        <Modal.Footer
          className={classnames('modal-footer', styles.modalFooter)}
        >
          <FooterButtons
            onCancel={onHide}
            isSubmitDisabled={isBusy}
            showSubmitTooltip={showTooltip}
          />
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default ProposalRespondModal;
